import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import Invoices from './Invoices';
import authService from './api-authorization/AuthorizeService';

interface SendInvoice {
    loading: boolean;
    tab: string;
    invoiceDetails: InvoiceDetails[];
    invoiceTotalFormatted: string;
    canEdit: boolean;
    canEditHirePeriod: boolean;
    canView: boolean;
    canSend: boolean;
    page: number;
    totalRecords: number;
    pageSize: number;
    reckonSwitchedOn: boolean;
    batchSending: boolean;
}

interface InvoiceDetails {
    id: number;
    companyId: number;          //job company id
    invoiceCompanyId: number;   //invoice company id => used to send to the correct Reckon
    customer: string;
    customerId: number;
    customerType: string;
    jobId: number;
    siteAddress: string;
    type: string;
    sendTo: string;
    batchNumber: string;
    reckonInvoiceId: string;
    canUpdateReckonInvoiceId: boolean;
    quoteNumber: string;
    hirePeriod: string;
    invoiceDate: string;
    dueDate: string;
    paidDate: string;
    paidDateActual: string;
    amount: string;
    errorMessage: string;
    status: string;
    statusId: number;
    canEdit: boolean;
    canDelete: boolean;
    invoiceStartDate: string;
    invoiceStartDateFormatted: string;
    isDirty: boolean;
    items: InvoiceItems[];
    showDetail: boolean;
    errors: any;
}

interface InvoiceItems {
    itemIndex: number;
    id: number;
    jobInvoiceId: number;
    branchIdent: string;
    item: string;
    itemId: number;
    itemTypeId: number;
    invItemType: string;
    invItemTypeId: number;
    quantityType: string;
    purchaseOrderNumber: string;
    additionalHireRate: string;
    rate: string;
    rateFormatted: string;
    gst: string;
    gstFormatted: string;
    chargeGst: boolean;
    amount: string;
    amountFormatted: string;
    description: string;
    status: number;
    invoiceStartDate: string;
    invoiceEndDate: string;
    hirePeriodDays: string;
    hirePeriod: string;
    dayRate: string;
    weekMonthRate: string;
    invoiceStartDateFormatted: string;
    invoiceEndDateFormatted: string;
    isDirty: boolean;
}

export class SendInvoicesData extends React.Component<RouteComponentProps<{}>, SendInvoice> {
    constructor(props: any) {
        super(props);

        this.state = {
            loading: true,
            tab: "Waiting",
            invoiceDetails: [],
            invoiceTotalFormatted: "$0.00",
            canEdit: false,
            canEditHirePeriod: false,
            canView: true,
            canSend: false,
            page: 1,
            totalRecords: 0,
            pageSize: 30,
            reckonSwitchedOn: false,
            batchSending: false
        };
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckInvoicingPermission?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.canView == true) {
                this.setState({ canEdit: res.data.canEdit, canEditHirePeriod: res.data.canEditHirePeriod, canSend: res.data.canSend });
                //enter the page that quickbooks needs to return to, id blank as not used via invoicing
                var page = {
                    page: "sendinvoices",
                    id: 0
                };
                //encode to base 64 so can retrieve it later
                var encoded = window.btoa(JSON.stringify(page));

                axios.get('api/Invoicing/ReckonInvoiceAuthoriseCheck?returnUrl=' + encoded + '&SubId=' + user.sub, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    //continue if Quick Books user is authorised or if Quick Books is switched off
                    this.setState({ reckonSwitchedOn: res.data.reckonSwitchedOn });
                    if (res.data.isAuthorised || !res.data.reckonSwitchedOn) {
                        this.getInvoices(this.state.tab, 1, true);
                    } else {
                        window.location.replace(res.data.reckonLoginUrl)
                    }
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else {
                this.setState({ canView: res.data.canView });
                //redirect to 403 permission denied
                this.props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    async getInvoices(invoiceType: string, page: number, getTotalRecords: boolean) {
        var totalRecords = getTotalRecords ? 0 : this.state.totalRecords;
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        if (invoiceType == "Waiting") {
            axios.get('api/Invoicing/GetAwaitingConfirmationInvoices?page=' + page + '&totalRecords=' + totalRecords + '&getTotalRecords=' + getTotalRecords + '&subId=' + user.sub, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                var invoiceDetails = res.data.invoices;
                for (var i = 0; i < invoiceDetails.length; i++) {
                    var invoice = invoiceDetails[i];
                    //invoice.errors = {};

                    for (var y = 0; y < invoice.items.length; y++) {
                        invoice.items[y].itemIndex = y;
                        invoice.items[y].amount = invoice.items[y].amount.toFixed(2);
                    }
                    //add blank row if canEdit invoice and user can edit
                    if (invoice.canEdit && this.state.canEdit) {
                        var newRow: InvoiceItems = {
                            itemIndex: invoice.items.length,
                            id: 0,
                            jobInvoiceId: invoice.id,
                            branchIdent: "",
                            item: "",
                            itemId: -1,
                            itemTypeId: -1,
                            invItemType: "",
                            invItemTypeId: -1,
                            quantityType: "",
                            purchaseOrderNumber: "",
                            additionalHireRate: "0.00",
                            rate: "0.00",
                            rateFormatted: "$0.00",
                            gst: "0.00",
                            gstFormatted: "$0.00",
                            chargeGst: true,
                            amount: "",
                            amountFormatted: "$0.00",
                            description: "",
                            status: 1,
                            invoiceStartDate: "",
                            invoiceEndDate: "",
                            invoiceStartDateFormatted: "",
                            invoiceEndDateFormatted: "",
                            hirePeriodDays: "",
                            hirePeriod: "",
                            dayRate: "",
                            weekMonthRate: "",
                            isDirty: false
                        }
                        invoice.items.push(newRow);
                    }
                }

                var sendingBatch = res.data.batchBeingSentToReckon;

                this.setState({ tab: invoiceType, invoiceDetails: invoiceDetails, invoiceTotalFormatted: res.data.invoiceTotalFormatted, page: page, totalRecords: res.data.totalRecords, pageSize: res.data.pageSize, batchSending: sendingBatch, loading: false });
            })
            .catch(error => {
                toast.error(error.message);
            });

        } else if (invoiceType == "Error") {
            axios.get('api/Invoicing/GetSentErrorInvoices?subId=' + user.sub, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                var invoiceDetails = res.data.invoices;
                for (var i = 0; i < invoiceDetails.length; i++) {
                    var invoice = invoiceDetails[i];
                    invoice.errors = {};

                    for (var y = 0; y < invoice.items.length; y++) {
                        invoice.items[y].itemIndex = y;
                    }
                }

                this.setState({ tab: invoiceType, invoiceDetails: invoiceDetails, invoiceTotalFormatted: res.data.invoiceTotalFormatted, page: page, totalRecords: res.data.totalRecords, pageSize: res.data.pageSize, loading: false });
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
    }

    tabSelected(tab: string) {
        this.getInvoices(tab, 1, true);
    }

    tabChanged = (e: any) => {
        this.tabSelected(e.target.value);
    }

    noChange = (e: any) => {
        //no changes as user doesn't actually click on the radio button
    }

    handleChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;
        this.setState(change);
    }

    submit = (data: any) => {
        var invoices: InvoiceDetails[] = data.invoiceDetails;
        if (invoices.length > 0) {
            this.saveInvoice(invoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    async saveInvoice(invoices: InvoiceDetails[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                customerId: invoices[inv].customerId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems,
            subId: user.sub
        }

        //save
        axios.post('api/Invoicing/SaveInvoiceItems', invDetails, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.getInvoices("Waiting", this.state.page, false);
            toast.success("Invoices Saved");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    saveHirePeriod = (data: any) => {
        var invoices: InvoiceDetails[] = data.invoiceDetails;

        if (invoices.length > 0) {
            this.saveInvoiceHirePeriod(invoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    async saveInvoiceHirePeriod(invoices: InvoiceDetails[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems,
            subId: user.sub
        }

        //save
        axios.post('api/Invoicing/SaveInvoiceItemHirePeriod', invDetails, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.getInvoices(this.state.tab, this.state.page, false);
            toast.success("Invoices Saved");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    updateInvoiceDetails = (updatedInvoices: InvoiceDetails[]) => {
        this.setState({ invoiceDetails: updatedInvoices });
    }

    pageChanged = (page: number) => {
        this.setState({ page: page });
        this.getInvoices(this.state.tab, page, false);
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        var contents = <Invoices invoiceDetails={this.state.invoiceDetails} invoiceTotalFormatted={this.state.invoiceTotalFormatted} showCustType={true} showJobInfo={true} showQuoteNo={false} showHirePeriod={this.state.tab == "Waiting" ? true : false} showStatus={false} showStartDate={false} showPaidDate={false} showPaid={false} showSend={true} showRemove={false} showSentTo={false} showReckonInvNo={true} showError={this.state.tab == "Waiting" ? false : true} showSent={this.state.tab == "Waiting" ? false : true} canEdit={this.state.tab == "Waiting" ? this.state.canEdit : false} canEditHirePeriod={this.state.tab == "Waiting" ? this.state.canEditHirePeriod : false} canSend={this.state.canSend} canUpdatePaid={false} page={this.state.page} pageSize={this.state.pageSize} totalRecords={this.state.totalRecords} pageChanged={this.pageChanged} beforeValid={this.updateInvoiceDetails} afterValid={this.submit} saveHirePeriod={this.saveHirePeriod} />

        return (
            <div>
                <div className={this.state.canView ? "hidden" : ""}>
                    <h2 className="error">You do not have permission to view this page!</h2>
                </div>
                <div className={this.state.canView ? "tabsComponent" : "hidden"} id="tab-container">
                    <div className={this.state.batchSending ? "newJobPricelist--prepaid" : "hidden"}>A batch is being sent to Reckon. Wait 5 mins and refresh the screen.</div>
                    <div className="tabsComponent__showDropdown">
                        <label className="input-group" htmlFor="scheduleTab">
                            <span className="label">Invoices</span>
                            <select className="select" id="scheduleTab" value={this.state.tab} onChange={this.tabChanged} disabled={this.state.batchSending} >
                                <option value="Waiting">Waiting</option>
                                <option value="Error">Error</option>
                            </select>
                        </label>
                    </div>

                    <div className={this.state.batchSending ? "hidden" : "tabsComponent__tabs tabsComponent__hideTabs"}>
                        <input type="radio" name="tabs" id="tab1" className="tabsComponent__control" checked={this.state.tab == 'Waiting'} onChange={(e) => this.noChange(e)}></input>
                        <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => this.tabSelected('Waiting')}>Waiting</label>
                        <input type="radio" name="tabs" id="tab2" className="tabsComponent__control" checked={this.state.tab == 'Error'} onChange={(e) => this.noChange(e)}></input>
                        <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => this.tabSelected('Error')}>Error</label>
                    </div>

                    <div className={this.state.batchSending ? "hidden" : "tabsComponent__content tabsComponent__content--selected"}>
                        {contents}
                    </div>
                </div>
            </div>
        );

    }

};